import React, { useState, useMemo } from "react";
import {
	Container,
	Stack,
	Typography,
	TextField,
	Button,
	Checkbox,
	FormControlLabel,
	Snackbar,
	Alert,
	Fade,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useChecklist } from "../useChecklist";

const EndRoster: React.FC = () => {
	const { rosterToday, isAdmin } = useAuth();
	const checklist = useChecklist();
	const navigate = useNavigate();
	const [localHours, setLocalHours] = useState<string>(
		checklist.hours !== null ? String(checklist.hours) : ""
	);
	const [localNote, setLocalNote] = useState<string>(checklist.note);
	const [localSendSms, setLocalSendSms] = useState<boolean>(
		checklist.sendSms
	);
	const [saving, setSaving] = useState(false);
	const [successOpen, setSuccessOpen] = useState(false);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);

	const disabled = !rosterToday && !isAdmin; // Not on duty and not admin (mirrors RosterToday logic)

	const decimalValid = useMemo(
		() => /^\d+\.\d+$/.test(localHours.trim()),
		[localHours]
	);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMsg(null);
		if (!decimalValid) {
			setErrorMsg("Timeteller må være et desimaltall, f.eks 2454.8");
			return;
		}
		const parsed = parseFloat(localHours);
		if (isNaN(parsed)) {
			setErrorMsg("Ugyldig timeteller");
			return;
		}
		setSaving(true);
		checklist.setHours(parsed);
		checklist.setNote(localNote);
		checklist.setSendSms(localSendSms);
		setSuccessOpen(true);
		setTimeout(() => navigate("/rosterToday"), 2500);
		setSaving(false);
	};

	if (rosterToday === undefined) return <></>; // still loading context

	if (disabled) {
		return (
			<Container>
				<Typography variant="body1" mt={4}>
					Du har ikke vakt i dag og kan derfor ikke avslutte vakt.
					Kontakt administrasjonen hvis du mener dette er feil.
				</Typography>
			</Container>
		);
	}

	return (
		<Fade in timeout={400}>
			<Container>
				<Snackbar
					open={successOpen}
					autoHideDuration={4000}
					onClose={() => setSuccessOpen(false)}
				>
					<Alert
						severity="success"
						variant="filled"
						sx={{ width: "100%" }}
					>
						Vakt avsluttet og lagret.
					</Alert>
				</Snackbar>
				<Stack gap={3} pt={2}>
					<Typography variant="h5">Avslutt vakt</Typography>
					<Typography variant="body2">
						Fyll inn timetelller (antall timer heisen har kjørt i
						dag) og eventuelle notater før du avslutter dagens siste
						vakt.
					</Typography>
					<form onSubmit={handleSubmit}>
						<Stack gap={2}>
							<TextField
								label="Timeteller (akkumulert timer, bruk desimal f.eks 2454.8)"
								type="text"
								value={localHours}
								onChange={(e) =>
									setLocalHours(
										e.target.value.replace(",", ".")
									)
								}
								required
								helperText={
									decimalValid
										? ""
										: "Må være format: heltall.desimal (eks 2454.8)"
								}
								error={localHours.length > 0 && !decimalValid}
								fullWidth
							/>
							<TextField
								label="Notat"
								placeholder="Skriv notat om dagens drift, feil eller observasjoner"
								multiline
								minRows={4}
								value={localNote}
								onChange={(e) => setLocalNote(e.target.value)}
								fullWidth
							/>
							{/*<FormControlLabel
								control={
									<Checkbox
										checked={localSendSms}
										onChange={(e) =>
											setLocalSendSms(e.target.checked)
										}
									/>
								}
								label="Send SMS til administratorer"
							/> */}
							{errorMsg && (
								<Alert severity="error">{errorMsg}</Alert>
							)}
							<Stack direction="row" gap={2}>
								<Button
									type="submit"
									variant="contained"
									disabled={saving || !decimalValid}
									color="primary"
								>
									{saving ? "Lagrer..." : "Avslutt vakt"}
								</Button>
								<Button
									variant="outlined"
									onClick={() => navigate("/rosterToday")}
								>
									Tilbake
								</Button>
							</Stack>
							<Typography variant="caption">
								Dersom du markerer notatet som Haster vil det gå
								en SMS til alle administratorer.
							</Typography>
						</Stack>
					</form>
				</Stack>
			</Container>
		</Fade>
	);
};

export default EndRoster;
